/**
 * Admin — per-client progress dashboard.
 *
 * Reuses the existing `GET /api/admin/training/clients/{user_id}/progress`
 * endpoint that already powers the coach's email digest. Renders the same
 * body-measurement chart the client sees in their portal, plus the recent
 * workout log entries and the AI-generated coach feedback.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from 'recharts';
import {
  Loader2, ChevronLeft, TrendingUp, Ruler, Dumbbell, MessageSquare, Activity,
  ClipboardList, Save, X, MessageCircle, Pencil, ChevronDown, ChevronUp, Calculator,
} from 'lucide-react';
import AdminClientCoachPanel from './AdminClientCoachPanel';
import ClientFoodLogCard from '../components/ClientFoodLogCard';
import { OneRmTrendChart, OneRmBar, oneRmEpley, percentZone } from '../components/TrainingSection';
import CalculatorSection from '../components/CalculatorSection';
import AestheticsCalculatorSection from '../components/AestheticsCalculatorSection';
import CaliperCalculator from '../components/CaliperCalculator';
import { adminTrainingPlanUpdate, adminClientMessageSend, adminClientAddMeasurement } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';

const ADMIN_KEY_STORAGE = 'orosz_admin_key_v1';
const API = process.env.REACT_APP_BACKEND_URL;

const fmtD = iso => (iso ? new Date(iso).toLocaleDateString('hu-HU', { year: '2-digit', month: '2-digit', day: '2-digit' }) : '—');
const fmtFull = iso => (iso ? new Date(iso).toLocaleString('hu-HU', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

/**
 * Compact inline measurement form — sits next to the chart header so the
 * coach can log a fresh measurement mid-session without opening a dialog
 * or scrolling up to the AdminClientCoachPanel. Fields depend on gender.
 */
const QuickMeasurementForm = ({ adminKey, userId, gender, onSaved }) => {
  const [weight, setWeight] = useState('');
  const [waist, setWaist] = useState('');
  const [third, setThird] = useState('');
  const [saving, setSaving] = useState(false);

  const thirdLabel = gender === 'male' ? 'Váll (cm)' : gender === 'female' ? 'Csípő (cm)' : 'Csípő (cm)';
  const thirdField = gender === 'male' ? 'shoulder_cm' : 'hip_cm';

  const submit = async (e) => {
    e.preventDefault();
    if (!userId) return;
    if (!weight && !waist && !third) {
      toast.error('Adj meg legalább egy értéket.');
      return;
    }
    setSaving(true);
    try {
      const payload = { note: 'admin_inline_form' };
      if (weight) payload.weight_kg = parseFloat(weight);
      if (waist) payload.waist_cm = parseFloat(waist);
      if (third) payload[thirdField] = parseFloat(third);
      await adminClientAddMeasurement(adminKey, userId, payload);
      toast.success('Mérés rögzítve.');
      setWeight(''); setWaist(''); setThird('');
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Mentés sikertelen.');
    } finally { setSaving(false); }
  };

  return (
    <form
      data-testid="admin-quick-measurement-form"
      onSubmit={submit}
      className="flex flex-wrap items-end gap-2"
    >
      <div>
        <label className="text-[9px] uppercase tracking-widest text-gray-500 block leading-none mb-1">Testsúly (kg)</label>
        <Input data-testid="quick-meas-weight" value={weight} onChange={(e) => setWeight(e.target.value)}
               type="number" step="0.1" placeholder="pl. 78.5"
               className="h-8 w-24 bg-gray-800 border-gray-700 text-white text-sm" />
      </div>
      <div>
        <label className="text-[9px] uppercase tracking-widest text-gray-500 block leading-none mb-1">Derék (cm)</label>
        <Input data-testid="quick-meas-waist" value={waist} onChange={(e) => setWaist(e.target.value)}
               type="number" step="0.5" placeholder="pl. 88"
               className="h-8 w-24 bg-gray-800 border-gray-700 text-white text-sm" />
      </div>
      <div>
        <label className="text-[9px] uppercase tracking-widest text-gray-500 block leading-none mb-1">{thirdLabel}</label>
        <Input data-testid="quick-meas-third" value={third} onChange={(e) => setThird(e.target.value)}
               type="number" step="0.5" placeholder={gender === 'male' ? 'pl. 122' : 'pl. 94'}
               className={`h-8 w-24 bg-gray-800 border-gray-700 text-white text-sm ${gender === 'male' ? 'border-purple-800/50' : 'border-rose-800/50'}`} />
      </div>
      <Button
        type="submit"
        data-testid="quick-meas-submit"
        size="sm"
        disabled={saving}
        className="h-8 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold px-3"
      >
        {saving ? <Loader2 className="animate-spin" size={12} /> : '+ Rögzítés'}
      </Button>
    </form>
  );
};

/**
 * Inline editor for the client's currently-active training plan. Renders the
 * exercise rows in a compact grid (week / muscle / name / sets / reps /
 * intensity / note) so the coach can tweak a prescription mid-session
 * without leaving the progress page. On save, POSTs the whole exercises
 * array back via `PUT /admin/training/plans/{id}` — the backend snapshots
 * the previous version, so existing workout logs still resolve.
 */
const ActivePlanEditor = ({ adminKey, plan, onSaved, workoutLogs = [] }) => {
  const [open, setOpen] = useState(true);
  const [rows, setRows] = useState(() => plan?.exercises || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setRows(plan?.exercises || []); }, [plan?.id, plan?.version]);

  // Human relative time for last-log badge.
  const humanAgo = (iso) => {
    if (!iso) return '';
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 3600 * 1000));
    if (days < 1) return 'ma';
    if (days === 1) return 'tegnap';
    if (days < 7) return `${days} napja`;
    if (days < 30) { const w = Math.floor(days / 7); return w === 1 ? '1 hete' : `${w} hete`; }
    if (days < 365) { const m = Math.floor(days / 30); return m === 1 ? '1 hónapja' : `${m} hónapja`; }
    const y = Math.floor(days / 365); return y === 1 ? '1 éve' : `${y} éve`;
  };

  // Best estimated 1RM per exercise (case-insensitive name match) — powers
  // the read-only Cél 70% / Csúcs 95% chips at the end of each plan row.
  const best1RmByName = useMemo(() => {
    const byName = {};
    (workoutLogs || []).forEach((log) => {
      const key = (log?.exercise_name || '').trim().toLowerCase();
      if (!key) return;
      let bestForLog = 0;
      const sets = Array.isArray(log?.sets) ? log.sets : null;
      if (sets && sets.length) {
        sets.forEach((s) => {
          const est = oneRmEpley(s?.kg, s?.reps);
          if (est != null && est > bestForLog) bestForLog = est;
        });
      } else {
        const est = oneRmEpley(log?.weight_kg, log?.reps);
        if (est != null) bestForLog = est;
      }
      if (bestForLog > 0 && (!byName[key] || bestForLog > byName[key])) byName[key] = bestForLog;
    });
    return byName;
  }, [workoutLogs]);

  // Last log per exercise for the "Utoljára: 65kg × 8 · 3 hete" badge.
  const lastLogByName = useMemo(() => {
    const byName = {};
    const sorted = [...(workoutLogs || [])].sort((a, b) =>
      new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime());
    sorted.forEach((log) => {
      const key = (log?.exercise_name || '').trim().toLowerCase();
      if (!key || byName[key]) return;
      const sets = Array.isArray(log?.sets) ? log.sets : null;
      let w = log?.weight_kg, r = log?.reps;
      if (sets && sets.length) {
        const top = [...sets].sort((a, b) => (parseFloat(b?.kg) || 0) - (parseFloat(a?.kg) || 0))[0];
        if (top) { w = top.kg; r = top.reps; }
      }
      if (w == null && r == null) return;
      byName[key] = { weight: w, reps: r, ago: humanAgo(log?.created_at) };
    });
    return byName;
  }, [workoutLogs]);

  const zoneKg = (name, pct) => {
    const b = best1RmByName[(name || '').trim().toLowerCase()];
    if (!b) return null;
    return Math.round((b * (pct / 100)) / 2.5) * 2.5;
  };
  const lastLog = (name) => lastLogByName[(name || '').trim().toLowerCase()];

  if (!plan) return null;

  const patch = (idx, field, value) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };
  const removeRow = (idx) => setRows(prev => prev.filter((_, i) => i !== idx));
  const addRow = () => setRows(prev => [...prev, {
    id: crypto.randomUUID?.() || String(Date.now()),
    week: '', muscle_group: '', name: '', sets: '', reps: '',
    intensity_pct: '', intensity_kg: '', tempo: '', rest: '',
    methods: '', note: '',
  }]);

  const save = async () => {
    setSaving(true);
    try {
      await adminTrainingPlanUpdate(adminKey, plan.id, { exercises: rows });
      toast.success('Edzésterv frissítve — az ügyfél a fiókjában látja.');
      onSaved?.();
    } catch (e) {
      toast.error('Mentés sikertelen: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="admin-active-plan-editor"
      className="bg-gray-900 border border-yellow-500/40 rounded-lg overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-800/50 transition"
      >
        <div className="flex items-center gap-2">
          <ClipboardList className="text-yellow-500" size={16} />
          <span className="text-xs font-bold uppercase tracking-widest text-gray-300">
            Aktív edzésterv — inline szerkeszthető
          </span>
          <span className="text-sm text-gray-400 ml-2">{plan.title}</span>
          <span className="text-[10px] text-yellow-500 bg-yellow-500/10 border border-yellow-500/30 rounded px-2 py-0.5 ml-1">
            v{plan.version || 1} · {rows.length} gyakorlat
          </span>
        </div>
        {open ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </button>

      {open && (
        <div className="border-t border-gray-800 p-4 space-y-3">
          <div className="hidden md:grid grid-cols-[60px_120px_1fr_60px_60px_80px_70px_70px_1fr_40px] gap-2 text-[10px] uppercase tracking-widest text-gray-500 px-1">
            <span>Hét</span><span>Izomcsop.</span><span>Gyakorlat</span>
            <span>Sor.</span><span>Ism.</span><span>%1RM / kg</span>
            <span className="text-emerald-400" title="Izomépítő minimum: becsült 1RM × 70%, 2,5 kg-ra kerekítve">Cél 70%</span>
            <span className="text-amber-400" title="Közel 1RM: becsült 1RM × 95%, 2,5 kg-ra kerekítve">Csúcs 95%</span>
            <span>Megjegyzés</span><span></span>
          </div>
          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {rows.map((r, idx) => {
              const kg70 = zoneKg(r.name, 70);
              const kg95 = zoneKg(r.name, 95);
              const ll = lastLog(r.name);
              return (
              <div
                key={r.id || idx}
                data-testid={`plan-row-${idx}`}
                className="grid grid-cols-2 md:grid-cols-[60px_120px_1fr_60px_60px_80px_70px_70px_1fr_40px] gap-2 items-center bg-gray-800/40 border border-gray-800 rounded p-2"
              >
                <Input data-testid={`plan-row-${idx}-week`} value={r.week || ''} onChange={e => patch(idx, 'week', e.target.value)} placeholder="A / B" className="h-9 bg-gray-900 border-gray-700 text-white text-sm" />
                <Input data-testid={`plan-row-${idx}-muscle`} value={r.muscle_group || ''} onChange={e => patch(idx, 'muscle_group', e.target.value)} placeholder="Mell" className="h-9 bg-gray-900 border-gray-700 text-white text-sm" />
                <div className="flex flex-col gap-0.5">
                  <Input data-testid={`plan-row-${idx}-name`} value={r.name || ''} onChange={e => patch(idx, 'name', e.target.value)} placeholder="Fekvenyomás" className="h-9 bg-gray-900 border-gray-700 text-white text-sm font-semibold" />
                  {ll && (
                    <div data-testid={`plan-row-${idx}-last-log`} className="text-[10px] text-emerald-400 italic px-1">
                      Utoljára: <strong>{ll.weight}kg × {ll.reps}</strong> · {ll.ago}
                    </div>
                  )}
                </div>
                <Input data-testid={`plan-row-${idx}-sets`} value={r.sets || ''} onChange={e => patch(idx, 'sets', e.target.value)} placeholder="3" className="h-9 bg-gray-900 border-gray-700 text-white text-sm" />
                <Input data-testid={`plan-row-${idx}-reps`} value={r.reps || ''} onChange={e => patch(idx, 'reps', e.target.value)} placeholder="8-10" className="h-9 bg-gray-900 border-gray-700 text-white text-sm" />
                <Input data-testid={`plan-row-${idx}-intensity`} value={r.intensity_kg || r.intensity_pct || ''} onChange={e => {
                  const v = e.target.value;
                  if (v.includes('%')) {
                    setRows(prev => prev.map((row, i) => i === idx ? { ...row, intensity_pct: v, intensity_kg: '' } : row));
                  } else {
                    setRows(prev => prev.map((row, i) => i === idx ? { ...row, intensity_kg: v, intensity_pct: '' } : row));
                  }
                }} placeholder="60 kg / 70%" className="h-9 bg-gray-900 border-gray-700 text-white text-sm" />
                <div data-testid={`plan-row-${idx}-zone-70`} className="h-9 flex items-center justify-center rounded text-xs font-bold bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" title="Cél 70% — izomépítő minimum">
                  {kg70 != null ? `${kg70} kg` : <span className="text-gray-600">—</span>}
                </div>
                <div data-testid={`plan-row-${idx}-zone-95`} className="h-9 flex items-center justify-center rounded text-xs font-bold bg-amber-500/10 text-amber-300 border border-amber-500/30" title="Csúcs 95% — közel 1RM">
                  {kg95 != null ? `${kg95} kg` : <span className="text-gray-600">—</span>}
                </div>
                <Input data-testid={`plan-row-${idx}-note`} value={r.note || ''} onChange={e => patch(idx, 'note', e.target.value)} placeholder="pl. lassú excentrikus" className="h-9 bg-gray-900 border-gray-700 text-white text-sm" />
                <button
                  type="button"
                  onClick={() => removeRow(idx)}
                  data-testid={`plan-row-${idx}-remove`}
                  className="h-9 w-9 flex items-center justify-center rounded text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/30"
                  title="Gyakorlat törlése"
                >
                  <X size={14} />
                </button>
              </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-gray-800">
            <button
              type="button"
              onClick={addRow}
              data-testid="plan-add-row"
              className="text-xs font-semibold text-yellow-400 hover:text-yellow-300 border border-yellow-500/30 hover:border-yellow-400 rounded px-3 py-1.5"
            >
              + Új gyakorlat
            </button>
            <div className="flex items-center gap-2">
              <Link
                to={`/admin/edzestervek?user=${plan.user_id}`}
                className="text-xs text-gray-400 hover:text-yellow-400 px-2"
              >
                Teljes szerkesztő →
              </Link>
              <Button
                onClick={save}
                disabled={saving}
                data-testid="plan-save-btn"
                className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold h-9 px-4"
              >
                {saving ? <><Loader2 className="animate-spin mr-1.5" size={14} /> Mentés…</> : <><Save size={14} className="mr-1.5" /> Mentés</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Small "add comment to this log" dialog. Uses the existing coach-messages
 * endpoint with a `log_id`, so the client sees a "Gábor: …" yellow bubble
 * under the workout log entry in their portal.
 */
const LogCommentDialog = ({ adminKey, userId, log, existingText, onSaved, onClose }) => {
  const [text, setText] = useState(existingText || '');
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!text.trim()) { toast.error('Írj be egy üzenetet.'); return; }
    setSending(true);
    try {
      await adminClientMessageSend(adminKey, userId, { message: text.trim(), log_id: log.id });
      toast.success('Megjegyzés elküldve — az ügyfél a fiókjában és emailben is látja.');
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error('Küldés sikertelen: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSending(false);
    }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent data-testid="log-comment-dialog" className="bg-gray-900 border-gray-700 text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white"><MessageCircle size={16} className="text-yellow-500" /> Megjegyzés a naplóhoz</DialogTitle>
          <DialogDescription className="text-gray-400">
            <span className="font-semibold text-gray-200">{log.exercise_name}</span> · {log.weight_kg}kg × {log.reps} · {fmtFull(log.created_at)}
            <br />Az üzenet megjelenik a kliens Fiókjában sárga „Gábor: …" bubble-ként, és emailben is kap értesítést.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          data-testid="log-comment-textarea"
          value={text}
          onChange={e => setText(e.target.value)}
          rows={4}
          maxLength={800}
          placeholder="pl. Szép technika, jövő héten +2.5 kg a fekvenyomáson. Az excentrikus fázisra figyelj kicsit jobban."
          className="bg-gray-800 border-gray-700 text-white"
        />
        <div className="text-[10px] text-gray-500 text-right">{text.length}/800</div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-gray-700 text-gray-300">Mégse</Button>
          <Button onClick={send} disabled={sending} data-testid="log-comment-send" className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold">
            {sending ? <><Loader2 className="animate-spin mr-1.5" size={14} /> Küldés…</> : <><Save size={14} className="mr-1.5" /> Küldés</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Tabbed calculators embedded on the admin progress page. Reuses the
 * three public calculator components (`CalculatorSection` — US Navy BF+TDEE,
 * `AestheticsCalculatorSection` — Adonis/Venus Index, `CaliperCalculator` —
 * Jackson-Pollock skinfolds) so the coach can measure the client mid-session
 * without leaving the progress view. Gender pre-fills from the client's
 * baseline so the male/female-only fields (hip/shoulder/skinfold sites)
 * are correct out of the box.
 *
 * After calculating, the coach clicks the existing "Új mérés rögzítése"
 * green button on the coach panel above to persist the numbers into the
 * client's body_measurements timeline — which then feeds the chart.
 */
const AdminCalculatorsPanel = ({ gender }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('bf'); // bf | index | caliper
  const TABS = [
    { id: 'bf',      label: 'Testzsír + TDEE',  desc: 'US Navy nyak/derék módszer + Mifflin-St Jeor kalória' },
    { id: 'index',   label: 'Aranymetszés',      desc: 'Adonis (férfi) / Venus (nő) Index' },
    { id: 'caliper', label: 'Kaliper',            desc: 'Jackson-Pollock bőrredő mérés' },
  ];

  return (
    <div
      data-testid="admin-calculators-panel"
      className="bg-gray-900 border border-purple-500/30 rounded-lg overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-800/50 transition"
      >
        <div className="flex items-center gap-2">
          <Calculator className="text-purple-400" size={16} />
          <span className="text-xs font-bold uppercase tracking-widest text-gray-300">
            Kalkulátorok — edzés közbeni méréshez
          </span>
          <span className="text-[10px] text-purple-300 bg-purple-500/10 border border-purple-500/30 rounded px-2 py-0.5 ml-1">
            {gender === 'male' ? 'Férfi' : gender === 'female' ? 'Nő' : 'Nem választva'}
          </span>
        </div>
        {open ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </button>

      {open && (
        <div className="border-t border-gray-800">
          <div className="flex flex-wrap gap-2 p-3 border-b border-gray-800 bg-gray-900/60">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`admin-calc-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`text-xs font-semibold px-3 py-2 rounded-md transition ${
                  tab === t.id
                    ? 'bg-purple-500 text-white'
                    : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
              >
                {t.label}
              </button>
            ))}
            <div className="text-[10px] text-gray-500 self-center ml-auto italic hidden md:block">
              {TABS.find((t) => t.id === tab)?.desc}
            </div>
          </div>
          <div className="text-xs text-gray-400 bg-gray-900/40 px-4 py-2.5 border-b border-gray-800">
            💡 <strong>Tipp</strong>: számold ki az értéket, jegyezd meg,
            majd fent a <span className="text-emerald-400 font-semibold">Új mérés rögzítése</span> gombbal
            add be az ügyfél haladási grafikonjához.
          </div>
          {/* Compress the marketing hero on the embedded calculators. */}
          <div className="admin-embedded-calc [&_section]:!py-6 [&_.py-24]:!py-6 [&_[data-testid='calculator-heading']]:!text-2xl [&_h2]:!text-2xl">
            {tab === 'bf'      && <CalculatorSection defaultGender={gender} />}
            {tab === 'index'   && <AestheticsCalculatorSection defaultGender={gender} />}
            {tab === 'caliper' && <CaliperCalculator defaultGender={gender} />}
          </div>
        </div>
      )}
    </div>
  );
};

export default function AdminClientProgressPage() {
  const nav = useNavigate();
  const adminKey = localStorage.getItem(ADMIN_KEY_STORAGE) || '';
  const [params, setParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState(params.get('user') || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [commentLog, setCommentLog] = useState(null);   // opens LogCommentDialog

  const reload = React.useCallback(() => {
    if (!userId || !adminKey) return;
    axios.get(`${API}/api/admin/training/clients/${userId}/progress`,
      { headers: { 'X-Admin-Key': adminKey } })
      .then(r => setData(r.data))
      .catch(() => { /* silent */ });
  }, [userId, adminKey]);

  useEffect(() => {
    if (!adminKey) { nav('/admin'); return; }
    axios.get(`${API}/api/admin/training/users`, { headers: { 'X-Admin-Key': adminKey } })
      .then(r => setUsers(r.data.users || []))
      .catch(() => toast.error('Nem sikerült betölteni a felhasználókat.'));
  }, [adminKey, nav]);

  useEffect(() => {
    if (!userId || !adminKey) { setData(null); return; }
    setLoading(true);
    axios.get(`${API}/api/admin/training/clients/${userId}/progress`,
      { headers: { 'X-Admin-Key': adminKey } })
      .then(r => setData(r.data))
      .catch(e => toast.error('Hiba: ' + (e?.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
    setParams({ user: userId });
  }, [userId, adminKey, setParams]);

  // Chart data mirrors the portal's TrainingSection exactly (gender-aware).
  const gender = data?.baseline?.gender;
  const chartData = useMemo(() => {
    if (!data) return [];
    const rows = [];
    if (data.baseline && (data.baseline.weight_kg || data.baseline.waist_cm)) {
      rows.push({
        date: `${fmtD(data.baseline.date)} (kezdés)`,
        Testsúly: data.baseline.weight_kg, Derék: data.baseline.waist_cm,
        Nyak: data.baseline.neck_cm, Csípő: data.baseline.hip_cm,
        Váll: data.baseline.shoulder_cm,
      });
    }
    (data.measurements || []).forEach(m => rows.push({
      date: fmtD(m.created_at),
      Testsúly: m.weight_kg, Derék: m.waist_cm, Nyak: m.neck_cm, Csípő: m.hip_cm,
      Váll: m.shoulder_cm,
    }));
    return rows;
  }, [data]);

  const latest = data?.measurements?.length ? data.measurements[data.measurements.length - 1] : null;
  const base = data?.baseline;
  const weightDiff = latest && base && latest.weight_kg && base.weight_kg
    ? (latest.weight_kg - base.weight_kg) : null;

  if (!adminKey) return null;

  const selectedUser = users.find(u => u.user_id === userId);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-gray-400 hover:text-white"><ChevronLeft size={20} /></Link>
            <Activity className="text-yellow-500" size={20} />
            <h1 className="text-lg font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Ügyfél haladás</h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {/* User picker */}
        <div className="bg-gray-900 rounded-lg p-4 border border-gray-800 mb-6 max-w-2xl">
          <label className="text-xs uppercase tracking-wider text-gray-400 block mb-2">Ügyfél kiválasztása</label>
          <select
            data-testid="admin-progress-user-picker"
            value={userId}
            onChange={e => setUserId(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white"
          >
            <option value="">— Válassz —</option>
            {users.map(u => (
              <option key={u.user_id} value={u.user_id}>
                {u.name || u.email} ({u.email})
              </option>
            ))}
          </select>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <Loader2 className="animate-spin mr-2" /> Töltés…
          </div>
        )}

        {!loading && userId && data && (
          <div className="space-y-6">
            {/* Coach action panel — cadence, add measurement / workout log,
                send report with personal note. Sits above the summary bar
                so the coach's daily flow starts here. */}
            <AdminClientCoachPanel
              adminKey={adminKey}
              userId={userId}
              user={selectedUser}
              gender={data?.baseline?.gender}
              onDataChanged={() => {
                // Re-fetch progress + users list so the freshly added
                // measurement / workout log reflects on the chart and the
                // updated cadence renders in the dropdown.
                reload();
                axios.get(`${API}/api/admin/training/users`, { headers: { 'X-Admin-Key': adminKey } })
                  .then(r => setUsers(r.data.users || [])).catch(() => { /* silent */ });
              }}
            />

            <ClientFoodLogCard adminKey={adminKey} userId={userId} />

            {/* Aktív edzésterv inline szerkeszthető — a coach a haladás
                oldalon módosíthatja az előre megírt gyakorlatokat,
                utána az ügyfél a saját fiókjában frissülve látja. */}
            <ActivePlanEditor
              adminKey={adminKey}
              plan={data.active_plan}
              workoutLogs={data.workout_logs || []}
              onSaved={reload}
            />

            {/* Kalkulátorok — a coach edzés közben méréskor használja őket.
                Testzsír, Adonis/Venus, Kaliper. Gender auto-prefill. */}
            <AdminCalculatorsPanel gender={data?.baseline?.gender} />

            {/* Summary bar */}
            <div className="bg-gradient-to-br from-yellow-500/10 to-emerald-500/10 border border-yellow-500/30 rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <div className="text-xs text-gray-400">Ügyfél</div>
                <div className="text-lg font-bold text-white">{selectedUser?.name || selectedUser?.email}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400">Kezdés</div>
                <div className="text-lg font-bold text-white">
                  {base?.date ? fmtD(base.date) : '—'}
                  <span className="text-xs text-gray-500 ml-1">{base?.weight_kg ? `· ${base.weight_kg}kg` : ''}</span>
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-400">Legutóbbi</div>
                <div className="text-lg font-bold text-white">
                  {latest ? fmtD(latest.created_at) : '—'}
                  <span className="text-xs text-gray-500 ml-1">{latest?.weight_kg ? `· ${latest.weight_kg}kg` : ''}</span>
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-400">Változás</div>
                <div className={`text-lg font-bold ${weightDiff == null ? 'text-gray-500' : weightDiff <= 0 ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {weightDiff == null ? '—' : `${weightDiff > 0 ? '+' : ''}${weightDiff.toFixed(1)} kg`}
                </div>
              </div>
            </div>

            {/* Body measurement chart + inline quick input */}
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-5" data-testid="admin-progress-chart">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 flex items-center gap-2">
                  <Ruler size={14} className="text-yellow-500" /> Testsúly és körméretek
                  {gender && (
                    <span className="text-[10px] text-gray-500 normal-case tracking-normal font-normal">
                      · {gender === 'male' ? 'férfi (Adonis)' : gender === 'female' ? 'nő (Venus)' : ''}
                    </span>
                  )}
                </h3>
                <QuickMeasurementForm
                  adminKey={adminKey}
                  userId={userId}
                  gender={gender}
                  onSaved={reload}
                />
              </div>
              {chartData.length >= 2 ? (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 0, left: -15 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} domain={['auto', 'auto']} />
                      <Tooltip
                        contentStyle={{ background: '#111827', border: '1px solid #374151', borderRadius: 6, color: '#fff' }}
                        labelStyle={{ color: '#9ca3af' }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12, color: '#9ca3af' }} />
                      <Line type="monotone" dataKey="Testsúly" stroke="#facc15" strokeWidth={2} connectNulls dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="Derék" stroke="#0ea5e9" strokeWidth={2} connectNulls dot={{ r: 3 }} />
                      {gender === 'male' && (
                        <Line type="monotone" dataKey="Váll" stroke="#a855f7" strokeWidth={1.75} connectNulls dot={{ r: 2.5 }} />
                      )}
                      {gender === 'female' && (
                        <Line type="monotone" dataKey="Csípő" stroke="#f43f5e" strokeWidth={1.75} connectNulls dot={{ r: 2.5 }} />
                      )}
                      {!gender && (<>
                        <Line type="monotone" dataKey="Nyak" stroke="#9ca3af" strokeWidth={1.5} connectNulls dot={{ r: 2 }} />
                        <Line type="monotone" dataKey="Csípő" stroke="#f43f5e" strokeWidth={1.5} connectNulls dot={{ r: 2 }} />
                      </>)}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-gray-500 text-sm py-8 text-center">
                  {chartData.length === 1
                    ? 'Csak a kezdőérték van meg — az ügyfél még nem rögzített új mérést.'
                    : 'Ehhez az ügyfélhez még nincsenek testmérési adatok.'}
                </div>
              )}
            </div>

            {/* Measurement history table */}
            {data.measurements?.length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-5">
                <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">Mérési előzmények ({data.measurements.length})</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-gray-500 uppercase tracking-wider border-b border-gray-800">
                      <tr>
                        <th className="text-left py-2 pr-4">Dátum</th>
                        <th className="text-right py-2 px-2">Testsúly</th>
                        <th className="text-right py-2 px-2">Derék</th>
                        {gender === 'male' ? (
                          <th className="text-right py-2 px-2">Váll</th>
                        ) : gender === 'female' ? (
                          <th className="text-right py-2 px-2">Csípő</th>
                        ) : (
                          <>
                            <th className="text-right py-2 px-2">Nyak</th>
                            <th className="text-right py-2 px-2">Csípő</th>
                          </>
                        )}
                        <th className="text-left py-2 pl-4">Megjegyzés</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...data.measurements].reverse().map(m => (
                        <tr key={m.id} className="border-b border-gray-800/50">
                          <td className="py-2 pr-4 text-gray-300">{fmtFull(m.created_at)}</td>
                          <td className="text-right px-2 text-white font-medium">{m.weight_kg ? `${m.weight_kg} kg` : '—'}</td>
                          <td className="text-right px-2 text-gray-300">{m.waist_cm ? `${m.waist_cm} cm` : '—'}</td>
                          {gender === 'male' ? (
                            <td className="text-right px-2 text-purple-300">{m.shoulder_cm ? `${m.shoulder_cm} cm` : '—'}</td>
                          ) : gender === 'female' ? (
                            <td className="text-right px-2 text-rose-300">{m.hip_cm ? `${m.hip_cm} cm` : '—'}</td>
                          ) : (
                            <>
                              <td className="text-right px-2 text-gray-400">{m.neck_cm ? `${m.neck_cm} cm` : '—'}</td>
                              <td className="text-right px-2 text-gray-400">{m.hip_cm ? `${m.hip_cm} cm` : '—'}</td>
                            </>
                          )}
                          <td className="pl-4 text-gray-400 text-xs">{m.note || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Recent workout logs — trend chart + per-row 1RM% / PR badge,
                mirroring what the client sees in their portal (dark theme). */}
            {data.workout_logs?.length > 0 && (() => {
              // Same chronological running-best + overall-best computation
              // as the client-facing TrainingSection.
              const logs = data.workout_logs;
              const chronological = [...logs].sort((a, b) => {
                const ta = new Date(a.created_at || 0).getTime();
                const tb = new Date(b.created_at || 0).getTime();
                return ta - tb;
              });
              const runningBest = {};
              const bestBeforeById = {};
              const overallBest = {};
              chronological.forEach((log) => {
                const key = (log.exercise_name || '').trim().toLowerCase();
                if (!key) return;
                bestBeforeById[log.id] = runningBest[key] || null;
                const est = oneRmEpley(log.weight_kg, log.reps);
                if (est != null) {
                  if (!runningBest[key] || est > runningBest[key]) runningBest[key] = est;
                  if (!overallBest[key] || est > overallBest[key]) overallBest[key] = est;
                }
              });
              return (
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-5" data-testid="admin-workout-log-section">
                <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 flex items-center gap-2 mb-4">
                  <Dumbbell size={14} className="text-yellow-500" /> Legutóbbi edzésnaplók ({logs.length})
                </h3>

                {/* 1RM trend chart — same component the client sees, in dark. */}
                <OneRmTrendChart workoutLogs={logs} theme="dark" />

                <div className="mt-5 space-y-3 max-h-[520px] overflow-y-auto pr-1">
                  {logs.slice(0, 30).map((l) => {
                    const key = (l.exercise_name || '').trim().toLowerCase();
                    const max1 = overallBest[key];
                    const currentEst = oneRmEpley(l.weight_kg, l.reps);
                    const pct = (max1 && l.weight_kg) ? (parseFloat(l.weight_kg) / max1) * 100 : null;
                    const zone = pct != null ? percentZone(pct) : null;
                    const bestBefore = bestBeforeById[l.id];
                    const isPr = !!(currentEst && bestBefore && currentEst > bestBefore);
                    // Existing coach comment (yellow "Gábor: …" bubble) —
                    // matches the client-side rendering in TrainingSection.
                    const existingComment = (data.messages || []).find(
                      m => m.log_id === l.id && m.sender === 'coach',
                    );
                    return (
                      <div
                        key={l.id}
                        data-testid={`admin-log-row-${l.id}`}
                        className="rounded-lg border border-gray-800 bg-gray-800/40 px-3 py-2"
                      >
                        <div className="flex flex-wrap items-center justify-between text-sm gap-2">
                          <span className="font-semibold text-white">{l.exercise_name}</span>
                          <span className="text-gray-300 flex items-center gap-2">
                            <span>
                              {l.sets?.length
                                ? l.sets.map(s => `${s.kg}kg×${s.reps} (RPE ${10 - s.rir})`).join(' · ')
                                : `${l.weight_kg ?? ''}${l.weight_kg ? 'kg × ' : ''}${l.reps ?? ''}${l.reps ? ' ism.' : ''}`}
                              <span className="text-gray-500 ml-2 text-xs">
                                {fmtFull(l.created_at)}{l.logged_by === 'coach' ? ' · általad írva' : ''}
                              </span>
                            </span>
                            <button
                              type="button"
                              data-testid={`admin-log-comment-btn-${l.id}`}
                              onClick={() => setCommentLog(l)}
                              title={existingComment ? 'Megjegyzés szerkesztése (új üzenetet küld)' : 'Megjegyzés hozzáfűzése'}
                              className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border ${existingComment ? 'text-yellow-300 border-yellow-500/40 bg-yellow-500/10 hover:bg-yellow-500/20' : 'text-gray-400 border-gray-700 hover:text-yellow-400 hover:border-yellow-500/40'}`}
                            >
                              {existingComment ? <><Pencil size={11} /> Megj.</> : <><MessageCircle size={11} /> +Megj.</>}
                            </button>
                          </span>
                        </div>

                        {pct != null && zone && (
                          <div
                            data-testid={`admin-onerm-panel-${l.id}`}
                            className="mt-2 rounded-md border border-gray-700 bg-gray-900/60 px-3 py-2"
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                              <div className="text-gray-300">
                                <span className="font-bold text-white">{Math.round(pct)}%</span>{' '}
                                <span className="text-gray-500">a becsült 1RM-ből</span>
                                <span className="ml-2 text-gray-500">
                                  · 1RM ≈ <span className="text-gray-200 font-semibold">{Math.round(max1)} kg</span>
                                </span>
                                {isPr && (
                                  <span className="ml-2 text-emerald-400 font-bold" data-testid={`admin-onerm-pr-${l.id}`}>
                                    🎉 új PR!
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-white ${zone.color}`}>
                                  {zone.label}
                                </span>
                                <span className="text-[10px] text-gray-400 font-semibold">RPE {zone.rpe}</span>
                              </div>
                            </div>
                            <OneRmBar percent={pct} />
                            {!zone.ok && (
                              <div className="mt-1.5 text-[11px] text-gray-500">
                                Az izomépítéshez érdemes legalább <span className="font-bold text-emerald-400">70%</span>-on dolgozni.
                              </div>
                            )}
                          </div>
                        )}

                        {existingComment && (
                          <div
                            data-testid={`admin-log-bubble-${l.id}`}
                            className="mt-2 rounded-lg bg-yellow-500/10 border border-yellow-500/30 px-3 py-1.5 text-xs text-gray-200"
                          >
                            <span className="font-bold text-yellow-300">Gábor:</span> {existingComment.message}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              );
            })()}

            {/* Coach AI feedback */}
            {data.feedback && (
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-5">
                <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 flex items-center gap-2 mb-4">
                  <TrendingUp size={14} className="text-yellow-500" /> Automata haladás-értékelés
                </h3>

                {data.feedback.headline && (
                  <div className="text-base font-semibold text-white mb-1">{data.feedback.headline}</div>
                )}
                <div className="text-xs text-gray-500 mb-4">
                  Szint: <span className="text-gray-300">{data.feedback.level || '—'}</span>
                  {data.feedback.split?.sessionsPerWeek ? ` · ${data.feedback.split.sessionsPerWeek} edzés/hét terv szerint` : ''}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Strength per exercise */}
                  {data.feedback.strength?.length > 0 && (
                    <div className="bg-gray-800/50 rounded p-3">
                      <div className="text-xs uppercase tracking-wider text-yellow-500 mb-2">Erőfejlődés (e1RM)</div>
                      <div className="space-y-2">
                        {data.feedback.strength.map((s, i) => (
                          <div key={i} className="text-sm">
                            <div className="flex items-center justify-between">
                              <span className="text-gray-200 font-medium">{s.exercise}</span>
                              <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                                s.verdict === 'fejlődés' ? 'bg-emerald-600/20 text-emerald-400' :
                                s.verdict === 'stagnálás' ? 'bg-amber-600/20 text-amber-400' :
                                s.verdict === 'visszaesés' ? 'bg-red-600/20 text-red-400' :
                                'bg-gray-700 text-gray-300'
                              }`}>
                                {s.verdict}{s.change_pct != null ? ` ${s.change_pct > 0 ? '+' : ''}${s.change_pct}%` : ''}
                              </span>
                            </div>
                            {s.message && <div className="text-xs text-gray-400 mt-0.5">{s.message}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Adherence */}
                  {data.feedback.adherence && (
                    <div className="bg-gray-800/50 rounded p-3">
                      <div className="text-xs uppercase tracking-wider text-blue-400 mb-2">Rendszeresség</div>
                      <div className="text-sm text-gray-300">
                        Terv szerint: <span className="text-white font-semibold">{data.feedback.adherence.planned_per_week}</span> edzés/hét
                      </div>
                      <div className="text-sm text-gray-300">
                        Utóbbi hét: <span className="text-white font-semibold">{data.feedback.adherence.last_week_sessions}</span> edzés
                      </div>
                      {data.feedback.adherence.message && (
                        <div className="text-xs text-gray-400 mt-1">{data.feedback.adherence.message}</div>
                      )}
                    </div>
                  )}

                  {/* Weight trend */}
                  {data.feedback.weight && (
                    <div className="bg-gray-800/50 rounded p-3">
                      <div className="text-xs uppercase tracking-wider text-emerald-400 mb-2">Testsúly-trend</div>
                      <div className="text-sm text-gray-300">
                        Verdikt: <span className={`font-semibold ${
                          data.feedback.weight.verdict === 'csökkenés' ? 'text-emerald-400' :
                          data.feedback.weight.verdict === 'növekedés' ? 'text-amber-400' :
                          'text-gray-300'
                        }`}>{data.feedback.weight.verdict}</span>
                        {data.feedback.weight.change != null && (
                          <span className="text-gray-400 ml-2">({data.feedback.weight.change > 0 ? '+' : ''}{data.feedback.weight.change} kg)</span>
                        )}
                      </div>
                      {data.feedback.weight.message && (
                        <div className="text-xs text-gray-400 mt-1">{data.feedback.weight.message}</div>
                      )}
                    </div>
                  )}

                  {/* Waist trend */}
                  {data.feedback.waist && data.feedback.waist.series?.length > 0 && (
                    <div className="bg-gray-800/50 rounded p-3">
                      <div className="text-xs uppercase tracking-wider text-rose-400 mb-2">Derék-trend</div>
                      <div className="text-sm text-gray-300">
                        Verdikt: <span className={`font-semibold ${
                          data.feedback.waist.verdict === 'csökkenés' ? 'text-emerald-400' :
                          data.feedback.waist.verdict === 'növekedés' ? 'text-amber-400' :
                          'text-gray-300'
                        }`}>{data.feedback.waist.verdict}</span>
                        {data.feedback.waist.change != null && (
                          <span className="text-gray-400 ml-2">({data.feedback.waist.change > 0 ? '+' : ''}{data.feedback.waist.change} cm)</span>
                        )}
                      </div>
                      {data.feedback.waist.message && (
                        <div className="text-xs text-gray-400 mt-1">{data.feedback.waist.message}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Deeplink to messaging */}
            <div className="flex flex-wrap gap-2 pt-2">
              <Link
                to={`/admin/riport/${userId}`}
                data-testid="admin-progress-report-link"
                className="inline-flex items-center gap-2 text-sm text-yellow-400 border border-yellow-500/30 bg-yellow-500/10 hover:bg-yellow-500/20 px-3 py-1.5 rounded"
              >
                <TrendingUp size={14} /> Havi riport →
              </Link>
              <Link
                to={`/admin/edzestervek?user=${userId}`}
                className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-yellow-400 px-3 py-1.5"
              >
                <Dumbbell size={14} /> Ügyfél edzésterve →
              </Link>
              <Link
                to={`/admin/etrendtervek?user=${userId}`}
                className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-yellow-400 px-3 py-1.5"
              >
                <MessageSquare size={14} /> Étrendszerkesztő →
              </Link>
            </div>
          </div>
        )}

        {!loading && !userId && (
          <div className="bg-gray-900 border border-dashed border-gray-700 rounded-lg p-16 text-center text-gray-500 max-w-2xl">
            <Activity size={40} className="mx-auto mb-4 opacity-50" />
            <p>Válassz egy ügyfelet a legördülő listából.</p>
            <p className="text-xs mt-2">Ellenőrizheted a testsúly + körméret grafikonját, mérési előzményeit és legutóbbi edzésnaplóit.</p>
          </div>
        )}

        {commentLog && (
          <LogCommentDialog
            adminKey={adminKey}
            userId={userId}
            log={commentLog}
            existingText={(data?.messages || []).find(m => m.log_id === commentLog.id && m.sender === 'coach')?.message || ''}
            onSaved={reload}
            onClose={() => setCommentLog(null)}
          />
        )}
      </main>
    </div>
  );
}
